import { Button, Grid, IconButton, Typography } from "@mui/material";
import {
  MRT_ColumnDef,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useEffect, useMemo, useState } from "react";
import "react-multi-date-picker/styles/colors/green.css";
import {
  CLIENT_INVOICE_EDIT_COLUMN_NAME,
  CLIENT_INVOICE_EDIT_LABEL_NAME,
} from "../ClientInvoiceConstants";
import { getDefaultMRTOptions } from "../../../dedicatedDelivery/DefaultMrtOptions";
import {
  ClientInvoiceEditResponse,
  RatecardClientInvoiceResponse,
} from "../../../../app/models/responseModels/ClientInvoiceResponses";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmModal from "../../../../components/common/modalPopup/ConfirmModal";
import { useSelector } from "react-redux";
import { RootState } from "../../../../app/features/store/store";
import { RatecardFdpInvoiceResponse } from "../../../../app/models/responseModels/FdpInvoiceResponses";

export type ClientInvoiceListProps = {
  editList: ClientInvoiceEditResponse[];
  handleOpenEdit: CallableFunction;
  handleDeleteInvoiceEdit: () => Promise<boolean>;
  setDeleteInvoiceEditIds: CallableFunction;
  setDeleteClientInvoiceId?: CallableFunction | null;
  invoice:
    | RatecardClientInvoiceResponse
    | RatecardFdpInvoiceResponse
    | undefined;
  isClientIdColumnVisible: boolean;
};

const ClientInvoiceEditList = (props: ClientInvoiceListProps) => {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  // const [deleteInvoiceEditIds, setDeleteInvoiceEditIds] = useState<number[]>(
  //   []
  // );

  // import base configruation for grid
  const defaultMRTOptions = getDefaultMRTOptions<ClientInvoiceEditResponse>();

  // map columns to show on grid
  const columns = useMemo<MRT_ColumnDef<ClientInvoiceEditResponse>[]>(
    () => [
      {
        accessorKey: "action",
        header: "Action",
        size: 50,
        Cell: ({ row }) => {
          return (
            <>
              <Button
                key={row.original.id}
                sx={{ m: 2 }}
                component="label"
                variant="contained"
                onClick={() =>
                  props.handleOpenEdit(
                    row.original.id,
                    row.original.storeId,
                    row.original.editRoute
                      ? row.original.editRoute.tripId
                      : null
                  )
                }
              >
                View / Edit
              </Button>
              <IconButton
                color="error"
                // disabled={
                //   props.editList.length - props.deleteEditIdList.length === 1
                //     ? true
                //     : props.deleteEditIdList.includes(row.original.id)
                // }
                onClick={() => {
                  setShowConfirmDelete(true);
                  props.setDeleteInvoiceEditIds([row.original.id]);
                  props?.setDeleteClientInvoiceId?.(
                    row.original.clientInvoiceId
                  );
                  // props.setDeleteEditIdList([
                  //   ...props.deleteEditIdList,
                  //   row.original.id,
                  // ]);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </>
          );
        },
      },
      {
        accessorKey: CLIENT_INVOICE_EDIT_COLUMN_NAME.ID,
        header: CLIENT_INVOICE_EDIT_LABEL_NAME.ID,
        size: 50,
        Cell: ({ row }) => row.original.id,
      },
      {
        accessorKey: CLIENT_INVOICE_EDIT_COLUMN_NAME.STORE_ID,
        header: CLIENT_INVOICE_EDIT_LABEL_NAME.STORE_ID,
        size: 50,
        Cell: ({ row }) =>
          props.invoice?.stores.find(
            (store) => store.id === row.original.storeId
          )?.storeId,
      },
      {
        accessorKey: CLIENT_INVOICE_EDIT_COLUMN_NAME.CLIENT_NAME,
        header: CLIENT_INVOICE_EDIT_LABEL_NAME.CLIENT_NAME,
        size: 50,
      },
      {
        accessorKey: CLIENT_INVOICE_EDIT_COLUMN_NAME.TRIP_ID,
        header: CLIENT_INVOICE_EDIT_LABEL_NAME.TRIP_ID,
        size: 50,
        Cell: ({ row }) =>
          row.original.editRoute && row.original.editRoute.tripId
            ? row.original.editRoute.tripId
            : null,
      },
      {
        accessorKey: CLIENT_INVOICE_EDIT_COLUMN_NAME.START_DATE,
        header: CLIENT_INVOICE_EDIT_LABEL_NAME.START_DATE,
        size: 50,
        Cell: ({ cell, row }) => row.original.editStartRange,
      },
      {
        accessorKey: CLIENT_INVOICE_EDIT_COLUMN_NAME.END_DATE,
        header: CLIENT_INVOICE_EDIT_LABEL_NAME.END_DATE,
        size: 50,
        Cell: ({ cell, row }) => row.original.editEndRange,
      },
      {
        accessorKey: CLIENT_INVOICE_EDIT_COLUMN_NAME.RATECARD_EDITED,
        header: CLIENT_INVOICE_EDIT_LABEL_NAME.RATECARD_EDITED,
        size: 50,
        Cell: ({ row }) => (row.original.isRatecardEdited ? "YES" : "NO"),
      },
      {
        accessorKey: CLIENT_INVOICE_EDIT_COLUMN_NAME.TOTAL_ORDERS,
        header: CLIENT_INVOICE_EDIT_LABEL_NAME.TOTAL_ORDERS,
        size: 50,
        Cell: ({ row }) =>
          row.original.editRoute && row.original.editRoute.totalOrders
            ? row.original.editRoute.totalOrders
            : null,
      },
      {
        accessorKey: CLIENT_INVOICE_EDIT_COLUMN_NAME.ORDERS_DELIVERED,
        header: CLIENT_INVOICE_EDIT_LABEL_NAME.ORDERS_DELIVERED,
        size: 50,
        Cell: ({ row }) =>
          row.original.editRoute && row.original.editRoute.ordersDelivered,
      },
      {
        accessorKey: CLIENT_INVOICE_EDIT_COLUMN_NAME.ORDERS_UNDELIVERED,
        header: CLIENT_INVOICE_EDIT_LABEL_NAME.ORDERS_UNDELIVERED,
        size: 50,
        Cell: ({ row }) =>
          row.original.editRoute && row.original.editRoute.ordersUndelivered,
      },
      {
        accessorKey:
          CLIENT_INVOICE_EDIT_COLUMN_NAME.ORDERS_UNDELIVERED_NO_ATTEMPT,
        header: CLIENT_INVOICE_EDIT_LABEL_NAME.ORDERS_UNDELIVERED_NO_ATTEMPT,
        size: 50,
        Cell: ({ row }) =>
          row.original.editRoute &&
          row.original.editRoute.ordersUndeliveredNoAttempt,
      },
    ],
    // [props.editList, props.currentEditId, props.deleteEditIdList]
    [props.editList]
  );

  // store grid configuration
  const table = useMaterialReactTable({
    ...defaultMRTOptions,
    columns: columns,
    data: props.editList,
    enableRowSelection: false,
    initialState: {
      ...defaultMRTOptions.initialState,
      columnVisibility: {
        clientName: props.isClientIdColumnVisible,
      },
    },
    state: {
      showProgressBars: false,
    },
    getRowId: (row) => row.id.toString(),
    muiTableBodyRowProps: ({ row }) => ({
      ...defaultMRTOptions.muiTableBodyRowProps,
      sx: {
        cursor: "pointer",
        "&.MuiTableRow-root.Mui-selected td": {
          bgcolor: "#ccff90",
          color: "black",
        },
        ":hover": { color: "black" },
      },
    }),

    renderBottomToolbarCustomActions: () => {
      return (
        <Typography variant="subtitle2" component="p">
          Total Rows: {props.editList.length}
        </Typography>
      );
    },
  });

  // const handleDeleteInvoiceEdit = async () => {
  //   const response = await ClientInvoiceAgent.deleteInvoiceEdit(
  //     deleteInvoiceEditIds.join(",")
  //   );

  //   return true;
  // };

  return (
    <>
      <Grid item xs={12} marginTop={2}>
        <MaterialReactTable table={table} />

        <ConfirmModal
          open={showConfirmDelete}
          onClose={() => {
            console.log(`onClose`);
            setShowConfirmDelete(false);
          }}
          asyncOperation={props.handleDeleteInvoiceEdit}
          closeModalAfterSubmit={true}
          closeSubmitModal={() => {
            console.log(`closeSubmitModal`);

            setShowConfirmDelete(false);
          }}
          confirmMessage={"Are you sure you want to delete this invoice edit?"}
        />
      </Grid>
    </>
  );
};
export default ClientInvoiceEditList;
